// UvunajiAssets.jsx
// Modern admin panel matching Majanga design system
// Shows ALL assets (Yield Crops, Livestock, Assets) — Dummy Products excluded (own tab)

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Plus, Search, Edit2, Trash2, ArrowLeft, Upload, Filter,
  RefreshCw, Package, TrendingUp, DollarSign, Layers,
  ChevronLeft, ChevronRight, X, Check, AlertTriangle,
  BarChart2, Calendar, Image, Eye, EyeOff,
} from "lucide-react";
import {
  createUvunajiAsset, deleteUvunajiAsset,
  fetchUvunajiAssets, IMG_BASE_URL, updateUvunajiAsset,
} from "./hooks/hooks";

// ─── Brand ────────────────────────────────────────────────────────────────────
const NAV  = "#001f3f";
const TEAL = "#0d9488";

// ─── Categories — Dummy Products intentionally excluded (has its own tab) ──────
const ASSET_CATEGORIES = [
  "Yield Crops",
  "Yield Livestock",
  "Yield Assets",
];

// ─── Category meta (color + icon) ────────────────────────────────────────────
const CAT_META = {
  "Yield Crops":     { color: "bg-green-100 text-green-700 border-green-200",  dot: "bg-green-500"  },
  "Yield Livestock": { color: "bg-amber-100 text-amber-700 border-amber-200",   dot: "bg-amber-500"  },
  "Yield Assets":    { color: "bg-blue-100 text-blue-700 border-blue-200",      dot: "bg-blue-500"   },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt   = (n) => parseFloat(n || 0).toLocaleString();
const fmtD  = (d) => d ? new Date(d).toLocaleDateString() : "—";

const CategoryBadge = ({ category }) => {
  const meta = CAT_META[category] || { color: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${meta.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {category}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color = "teal" }) => {
  const gradients = {
    teal: "from-teal-500 to-teal-600",
    navy: "from-[#001f3f] to-[#002d5a]",
    gold: "from-amber-400 to-amber-500",
    green:"from-green-500 to-green-600",
  };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradients[color]} flex items-center justify-center shadow-md flex-shrink-0`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

// ─── Validation ───────────────────────────────────────────────────────────────
const baseSchema = {
  asset_name:       Yup.string().max(255).required("Required"),
  category:         Yup.string().oneOf(ASSET_CATEGORIES).required("Required"),
  investing_price:  Yup.number().min(0).required("Required"),
  total_earning:    Yup.number().min(0).required("Required"),
  earning_cycle:    Yup.number().min(0.1).required("Required"),
  lol:              Yup.number().min(0).max(1).required("Required (0–1)"),
  available_quantity: Yup.number().min(0).required("Required"),
};

const createSchema = Yup.object({
  ...baseSchema,
  asset_image: Yup.mixed()
    .required("Image required")
    .test("type", "JPEG, PNG or GIF only", (v) =>
      !v || ["image/jpeg", "image/png", "image/gif"].includes(v?.type)
    ),
});
const updateSchema = Yup.object(baseSchema);

// ─── Form field ───────────────────────────────────────────────────────────────
const Field = ({ label, name, type = "text", fmk, placeholder, prefix, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      {label}
    </label>
    {hint && <p className="text-[10px] text-slate-400 mb-1">{hint}</p>}
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-medium">
          {prefix}
        </span>
      )}
      <input
        type={type}
        name={name}
        value={fmk.values[name]}
        onChange={fmk.handleChange}
        onBlur={fmk.handleBlur}
        placeholder={placeholder}
        step={type === "number" ? "any" : undefined}
        className={`w-full ${prefix ? "pl-14" : "pl-3"} pr-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all ${
          fmk.touched[name] && fmk.errors[name] ? "border-red-400 bg-red-50" : "border-slate-200 bg-white"
        }`}
      />
    </div>
    {fmk.touched[name] && fmk.errors[name] && (
      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
        <AlertTriangle size={10} /> {fmk.errors[name]}
      </p>
    )}
  </div>
);

// ─── Category select field ─────────────────────────────────────────────────────
const CatField = ({ fmk }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      Category
    </label>
    <select
      name="category"
      value={fmk.values.category}
      onChange={fmk.handleChange}
      onBlur={fmk.handleBlur}
      className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white ${
        fmk.touched.category && fmk.errors.category ? "border-red-400 bg-red-50" : "border-slate-200"
      }`}
    >
      <option value="">— Select Category —</option>
      {ASSET_CATEGORIES.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>
    {fmk.touched.category && fmk.errors.category && (
      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
        <AlertTriangle size={10} /> {fmk.errors.category}
      </p>
    )}
  </div>
);

// ─── Asset Form ───────────────────────────────────────────────────────────────
const AssetForm = ({ fmk, isUpdate = false, onCancel }) => (
  <form onSubmit={fmk.handleSubmit} className="space-y-5">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Asset Name"          name="asset_name"          fmk={fmk} placeholder="e.g. Mahindi Dodoma" />
      <CatField fmk={fmk} />
      <Field label="Investing Price"     name="investing_price"     fmk={fmk} type="number" prefix="TZS" placeholder="0.00" />
      <Field label="Total Earning"       name="total_earning"       fmk={fmk} type="number" prefix="TZS" placeholder="0.00" />
      <Field label="Earning Cycle (Yrs)" name="earning_cycle"       fmk={fmk} type="number" placeholder="e.g. 1.5" hint="Decimal accepted e.g. 0.6 = ~7 months" />
      <Field label="Lol (Platform Fee)"  name="lol"                 fmk={fmk} type="number" placeholder="e.g. 0.05 = 5%" hint="Enter as decimal between 0 and 1" />
      <Field label="Available Quantity"  name="available_quantity"  fmk={fmk} type="number" placeholder="Units available" />
    </div>

    {/* Image upload */}
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        Asset Image {isUpdate && <span className="font-normal text-slate-400">(leave blank to keep existing)</span>}
      </label>
      <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-xl py-8 px-4 cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-all group">
        <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-teal-100 flex items-center justify-center mb-3 transition-colors">
          <Upload size={20} className="text-slate-400 group-hover:text-teal-500 transition-colors" />
        </div>
        <p className="text-sm font-medium text-slate-600 group-hover:text-teal-600">Click to upload image</p>
        <p className="text-xs text-slate-400 mt-1">JPEG, PNG or GIF</p>
        <input
          type="file"
          hidden
          accept="image/*"
          onChange={(e) => fmk.setFieldValue("asset_image", e.currentTarget.files[0])}
          onBlur={fmk.handleBlur}
        />
      </label>
      {fmk.touched.asset_image && fmk.errors.asset_image && (
        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
          <AlertTriangle size={10} /> {fmk.errors.asset_image}
        </p>
      )}
      {fmk.values.asset_image && (
        <div className="mt-3 flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
            <img
              src={URL.createObjectURL(fmk.values.asset_image)}
              alt="preview"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 truncate">{fmk.values.asset_image.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">{(fmk.values.asset_image.size / 1024).toFixed(0)} KB</p>
          </div>
          <button
            type="button"
            onClick={() => fmk.setFieldValue("asset_image", null)}
            className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>

    {/* Computed preview */}
    {fmk.values.investing_price && fmk.values.total_earning && fmk.values.earning_cycle && fmk.values.lol && (
      <div className="bg-gradient-to-r from-slate-50 to-teal-50 rounded-xl border border-teal-100 p-4">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
          <BarChart2 size={12} className="text-teal-500" />
          Computed Earnings Preview
        </p>
        <div className="grid grid-cols-3 gap-3">
          {(() => {
            const price   = parseFloat(fmk.values.investing_price) || 0;
            const earning = parseFloat(fmk.values.total_earning)    || 0;
            const cycle   = parseFloat(fmk.values.earning_cycle)    || 1;
            const lol     = parseFloat(fmk.values.lol)              || 0;
            const daily   = earning / (cycle * 366);
            const netDaily= daily * (1 - lol);
            const monthly = netDaily * 30;
            return [
              { label: "Daily Income",    value: `TZS ${fmt(daily.toFixed(2))}` },
              { label: "Net Daily",       value: `TZS ${fmt(netDaily.toFixed(2))}` },
              { label: "Net Monthly",     value: `TZS ${fmt(monthly.toFixed(2))}` },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">{s.label}</p>
                <p className="text-sm font-bold text-teal-700 mt-0.5">{s.value}</p>
              </div>
            ));
          })()}
        </div>
      </div>
    )}

    {/* Actions */}
    <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
      )}
      <button
        type="submit"
        disabled={fmk.isSubmitting}
        className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 disabled:opacity-50 transition-all"
        style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}
      >
        {fmk.isSubmitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Check size={15} />
            {isUpdate ? "Update Asset" : "Add Asset"}
          </>
        )}
      </button>
    </div>
  </form>
);

// ─── Asset Detail Panel ───────────────────────────────────────────────────────
const AssetDetail = ({ asset, onClose }) => {
  const rows = [
    ["Category",           <CategoryBadge category={asset.category} />],
    ["Investing Price",    `TZS ${fmt(asset.investing_price)}`],
    ["Earning Cycle",      `${asset.earning_cycle} yrs`],
    ["Available Qty",      <span className={`font-bold ${asset.available_quantity === 0 ? "text-red-600" : asset.available_quantity < 10 ? "text-amber-600" : "text-green-600"}`}>{asset.available_quantity} units</span>],
    ["Daily Income",       `TZS ${fmt(asset.daily_income)}`],
    ["Daily Charges",      `TZS ${fmt(asset.daily_charges)}`],
    ["Net Daily Income",   `TZS ${fmt(asset.net_daily_income)}`],
    ["Monthly Income",     `TZS ${fmt(asset.monthly_income)}`],
    ["Net Monthly",        `TZS ${fmt(asset.net_monthly_income)}`],
    ["Total Cycle Earning",`TZS ${fmt(asset.total_cycle_earning)}`],
    ["Net Cycle Earning",  `TZS ${fmt(asset.total_net_cycle_earning)}`],
    ["Published",          fmtD(asset.published_at)],
  ];
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-slate-800">{asset.asset_name}</h3>
            <CategoryBadge category={asset.category} />
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex gap-6">
            {/* Image */}
            <div className="w-48 h-48 rounded-xl overflow-hidden border border-slate-200 flex-shrink-0 bg-slate-100">
              <img src={`${IMG_BASE_URL}${asset.asset_image}`} alt={asset.asset_name} className="w-full h-full object-cover" />
            </div>
            {/* Details */}
            <div className="flex-1 space-y-0">
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between items-center py-2.5 border-b border-slate-50">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
                  <span className="text-sm font-medium text-slate-800">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
function UvunajiAssets() {
  const qc = useQueryClient();

  const { data: rawAssets = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["uvunajiAssets"],
    queryFn:  fetchUvunajiAssets,
  });

  // Filter out Dummy Products — they belong to the Dummy tab
  const assets = (Array.isArray(rawAssets) ? rawAssets : []).filter(
    (a) => a.category !== "Yield Dummy Products"
  );

  // ── Local state ────────────────────────────────────────────────────────────
  const [view,         setView]        = useState("list");  // list | create | edit | detail
  const [selectedItem, setSelectedItem]= useState(null);
  const [detailAsset,  setDetailAsset] = useState(null);
  const [search,       setSearch]      = useState("");
  const [catFilter,    setCatFilter]   = useState("all");
  const [sortBy,       setSortBy]      = useState("date");
  const [page,         setPage]        = useState(0);
  const [rowsPerPage,  setRowsPerPage] = useState(10);
  const [toast,        setToast]       = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Filtered + sorted ─────────────────────────────────────────────────────
  const filtered = assets
    .filter((a) => catFilter === "all" || a.category === catFilter)
    .filter((a) =>
      !search ||
      a.asset_name?.toLowerCase().includes(search.toLowerCase()) ||
      a.category?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) =>
      sortBy === "name"
        ? a.asset_name.localeCompare(b.asset_name)
        : new Date(b.published_at) - new Date(a.published_at)
    );

  const paginated   = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalPages  = Math.ceil(filtered.length / rowsPerPage);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalUnits  = assets.reduce((s, a) => s + (a.available_quantity || 0), 0);
  const avgNetDaily = assets.length
    ? assets.reduce((s, a) => s + parseFloat(a.net_daily_income || 0), 0) / assets.length
    : 0;

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: createUvunajiAsset,
    onSuccess: () => {
      qc.invalidateQueries(["uvunajiAssets"]);
      refetch();
      setView("list");
      showToast("Asset added successfully!");
    },
    onError: () => showToast("Failed to add asset.", "error"),
  });

  const updateMut = useMutation({
    mutationFn: updateUvunajiAsset,
    onSuccess: () => {
      qc.invalidateQueries(["uvunajiAssets"]);
      refetch();
      setView("list");
      showToast("Asset updated!");
    },
    onError: () => showToast("Failed to update asset.", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: deleteUvunajiAsset,
    onSuccess: () => {
      qc.invalidateQueries(["uvunajiAssets"]);
      refetch();
      showToast("Asset deleted.");
    },
    onError: () => showToast("Failed to delete asset.", "error"),
  });

  // ── Formik: create ────────────────────────────────────────────────────────
  const createFmk = useFormik({
    initialValues: {
      asset_name: "", category: "", asset_image: null,
      investing_price: "", total_earning: "", earning_cycle: "",
      lol: "", available_quantity: "",
    },
    validationSchema: createSchema,
    onSubmit: (values) => createMut.mutate(values),
  });

  // ── Formik: update ────────────────────────────────────────────────────────
  const updateFmk = useFormik({
    initialValues: {
      asset_name: "", category: "", asset_image: null,
      investing_price: "", total_earning: "", earning_cycle: "",
      lol: "", available_quantity: "",
    },
    validationSchema: updateSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (selectedItem?.id) updateMut.mutate({ id: selectedItem.id, data: values });
    },
  });

  const openEdit = (asset) => {
    setSelectedItem(asset);
    updateFmk.setValues({
      asset_name:         asset.asset_name        || "",
      category:           asset.category          || "",
      asset_image:        null,
      investing_price:    asset.investing_price   || "",
      total_earning:      "",   // not stored on asset — user must re-enter
      earning_cycle:      asset.earning_cycle     || "",
      lol:                "",   // not stored — user must re-enter
      available_quantity: asset.available_quantity|| "",
    });
    setView("edit");
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this asset? This action cannot be undone.")) return;
    deleteMut.mutate(id);
  };

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (isLoading) return (
    <div className="flex flex-col items-center justify-center py-24 text-teal-600 gap-3">
      <div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm font-medium">Loading assets...</p>
    </div>
  );

  if (isError) return (
    <div className="flex items-center justify-center py-24">
      <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md text-center">
        <AlertTriangle size={32} className="text-red-500 mx-auto mb-3" />
        <h3 className="font-bold text-red-800 mb-1">Failed to load assets</h3>
        <p className="text-sm text-red-600 mb-4">Please check your connection and try again.</p>
        <button onClick={() => refetch()} className="px-4 py-2 text-sm text-white rounded-xl" style={{ background: NAV }}>
          Try Again
        </button>
      </div>
    </div>
  );

  // ── CREATE VIEW ────────────────────────────────────────────────────────────
  if (view === "create") return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => setView("list")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5 transition-colors">
        <ArrowLeft size={15} /> Back to Assets
      </button>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
            <Plus size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800">Add New Asset</h2>
            <p className="text-xs text-slate-400">Register a new investment asset with earnings configuration</p>
          </div>
        </div>
        <AssetForm fmk={createFmk} onCancel={() => setView("list")} />
      </div>
    </div>
  );

  // ── EDIT VIEW ──────────────────────────────────────────────────────────────
  if (view === "edit" && selectedItem) return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => setView("list")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5 transition-colors">
        <ArrowLeft size={15} /> Back to Assets
      </button>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
            <Edit2 size={16} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800">Edit: {selectedItem.asset_name}</h2>
            <p className="text-xs text-slate-400">Re-enter Total Earning and Lol to recalculate financials</p>
          </div>
        </div>
        <AssetForm fmk={updateFmk} isUpdate onCancel={() => setView("list")} />
      </div>
    </div>
  );

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Header strip ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Uvunaji Assets</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Yield Crops · Livestock · Assets — <span className="text-teal-600 font-medium">Dummy Products managed separately</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 transition-colors">
            <RefreshCw size={15} />
          </button>
          <button
            onClick={() => setView("create")}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-sm hover:opacity-90 transition-all"
            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}
          >
            <Plus size={14} /> Add Asset
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package}    label="Total Assets"      value={assets.length}               color="navy" />
        <StatCard icon={Layers}     label="Available Units"   value={totalUnits.toLocaleString()} color="teal" />
        <StatCard icon={TrendingUp} label="Avg Net Daily"     value={`TZS ${fmt(avgNetDaily.toFixed(0))}`} sub="per asset" color="gold" />
        <StatCard icon={DollarSign} label="Categories"        value={ASSET_CATEGORIES.length}     color="green" />
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or category..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:bg-white transition-all"
            />
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-2">
            <Filter size={13} className="text-slate-400" />
            <div className="flex gap-1.5">
              {["all", ...ASSET_CATEGORIES].map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setCatFilter(cat); setPage(0); }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all ${
                    catFilter === cat
                      ? "text-white border-transparent"
                      : "bg-white border-slate-200 text-slate-600 hover:border-teal-300"
                  }`}
                  style={catFilter === cat ? { background: `linear-gradient(135deg, ${NAV}, ${TEAL})` } : {}}
                >
                  {cat === "all" ? "All" : cat.replace("Yield ", "")}
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-300"
          >
            <option value="date">Latest first</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-50">
          <span className="text-xs text-slate-400">
            Showing <span className="font-semibold text-slate-600">{filtered.length}</span> of {assets.length} assets
            {catFilter !== "all" && <span className="text-teal-600"> · {catFilter}</span>}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 py-20 text-center">
          <Package size={36} className="mx-auto mb-3 text-slate-200" />
          <p className="text-slate-500 font-medium">No assets found</p>
          <p className="text-xs text-slate-400 mt-1">
            {search || catFilter !== "all" ? "Try adjusting your filters" : "Click 'Add Asset' to get started"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Asset", "Category", "Qty", "Cycle", "Price (TZS)", "Daily Income", "Net Daily", "Net Monthly", "Published", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.map((asset) => (
                  <tr key={asset.id} className="hover:bg-slate-50/60 transition-colors group">

                    {/* Asset name + image */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl overflow-hidden border border-slate-200 flex-shrink-0 bg-slate-100">
                          <img
                            src={`${IMG_BASE_URL}${asset.asset_image}`}
                            alt={asset.asset_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">{asset.asset_name}</p>
                          <p className="text-[10px] text-slate-400">ID #{asset.id}</p>
                        </div>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3.5">
                      <CategoryBadge category={asset.category} />
                    </td>

                    {/* Quantity */}
                    <td className="px-4 py-3.5">
                      <span className={`text-sm font-bold ${
                        asset.available_quantity === 0   ? "text-red-600"    :
                        asset.available_quantity < 10    ? "text-amber-600"  :
                        "text-green-600"
                      }`}>
                        {asset.available_quantity ?? 0}
                      </span>
                      <span className="text-xs text-slate-400 ml-1">units</span>
                    </td>

                    {/* Cycle */}
                    <td className="px-4 py-3.5 text-sm text-slate-600">
                      <span className="font-semibold">{asset.earning_cycle}</span>
                      <span className="text-xs text-slate-400 ml-1">yrs</span>
                    </td>

                    {/* Price */}
                    <td className="px-4 py-3.5 text-sm font-semibold text-slate-800 whitespace-nowrap">
                      {fmt(asset.investing_price)}
                    </td>

                    {/* Daily Income */}
                    <td className="px-4 py-3.5 text-sm text-slate-700 whitespace-nowrap">
                      {fmt(asset.daily_income)}
                    </td>

                    {/* Net Daily */}
                    <td className="px-4 py-3.5">
                      <span className="text-sm font-semibold text-teal-600 whitespace-nowrap">
                        {fmt(asset.net_daily_income)}
                      </span>
                    </td>

                    {/* Net Monthly */}
                    <td className="px-4 py-3.5 text-sm text-slate-700 whitespace-nowrap">
                      {fmt(asset.net_monthly_income)}
                    </td>

                    {/* Published */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar size={11} />
                        {fmtD(asset.published_at)}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setDetailAsset(asset)}
                          className="p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => openEdit(asset)}
                          className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id)}
                          disabled={deleteMut.isLoading}
                          className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                className="px-2 py-1 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
              >
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-xs text-slate-400">
                {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filtered.length)} of {filtered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
                className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs font-medium text-slate-600 px-2">
                {page + 1} / {totalPages || 1}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailAsset && <AssetDetail asset={detailAsset} onClose={() => setDetailAsset(null)} />}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl text-white text-sm font-medium ${
          toast.type === "success" ? "bg-green-600" : "bg-red-600"
        }`}>
          {toast.type === "success" ? <Check size={16} /> : <X size={16} />}
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-2 opacity-70 hover:opacity-100"><X size={13} /></button>
        </div>
      )}
    </div>
  );
}

export default UvunajiAssets;
// import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// import React, { useState } from 'react'
// import { createUvunajiAsset, deleteUvunajiAsset, fetchUvunajiAssets, IMG_BASE_URL, updateUvunajiAsset } from './hooks/hooks';
// import { useFormik } from 'formik';
// import * as Yup from 'yup';
// import { FiArrowLeft, FiUpload, FiEdit2, FiTrash2, FiPlus, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// // Validation Schema using Yup
// const validationSchema = Yup.object({
//     asset_name: Yup.string()
//         .max(255, "Must be 255 characters or less")
//         .required("Required"),
//     category: Yup.string()
//         .max(100, "Must be 100 characters or less")
//         .required("Required"),
//     investing_price: Yup.number()
//         .min(0, "Price must be positive")
//         .required("Required"),
//     total_earning: Yup.number()
//         .min(0, "Total earning must be positive")
//         .required("Required"),
//     earning_cycle: Yup.number()
//         .required("Required"),
//     lol: Yup.number()
//         .min(0, "Value must be positive")
//         .required("Required"),
//     available_quantity: Yup.number()
//         .min(1, "Quantity must be at least 1")
//         .required("Required"),
//     asset_image: Yup.mixed()
//       .required('Image is required')
//       .test('fileType', 'Only image files are allowed', (value) => {
//         if (value) {
//           return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
//         }
//         return true;
//       }),
// });

// const upValidationSchema = Yup.object({
//     asset_name: Yup.string()
//         .max(255, "Must be 255 characters or less")
//         .required("Required"),
//     category: Yup.string()
//         .max(100, "Must be 100 characters or less")
//         .required("Required"),
//     investing_price: Yup.number()
//         .min(0, "Price must be positive")
//         .required("Required"),
//     total_earning: Yup.number()
//         .min(0, "Total earning must be positive")
//         .required("Required"),
//     earning_cycle: Yup.number()
//         .required("Required"),
//     lol: Yup.number()
//         .min(0, "Value must be positive")
//         .required("Required"),
//     available_quantity: Yup.number()
//         .min(0, "Quantity cannot be negative")
//         .required("Required"),
// });

// // Form Field Component - MOVED OUTSIDE to prevent re-creation
// const FormField = ({ label, name, type = "text", formikInstance, placeholder, startAdornment }) => (
//     <div className="w-full">
//         <label className="block text-xs font-medium text-gray-700 uppercase mb-1">
//             {label}
//         </label>
//         <div className="relative">
//             {startAdornment && (
//                 <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">
//                     {startAdornment}
//                 </span>
//             )}
//             <input
//                 type={type}
//                 name={name}
//                 value={formikInstance.values[name]}
//                 onChange={formikInstance.handleChange}
//                 onBlur={formikInstance.handleBlur}
//                 placeholder={placeholder}
//                 className={`w-full ${startAdornment ? 'pl-16' : 'pl-3'} pr-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-900 focus:border-blue-900 ${
//                     formikInstance.touched[name] && formikInstance.errors[name] 
//                         ? 'border-red-500' 
//                         : 'border-gray-300'
//                 }`}
//             />
//         </div>
//         {formikInstance.touched[name] && formikInstance.errors[name] && (
//             <p className="mt-1 text-xs text-red-600 capitalize">{formikInstance.errors[name]}</p>
//         )}
//     </div>
// );

// // Select Field Component - MOVED OUTSIDE to prevent re-creation
// const SelectField = ({ label, name, options, formikInstance }) => (
//     <div className="w-full">
//         <label className="block text-xs font-medium text-gray-700 uppercase mb-1">
//             {label}
//         </label>
//         <select
//             name={name}
//             value={formikInstance.values[name]}
//             onChange={formikInstance.handleChange}
//             onBlur={formikInstance.handleBlur}
//             className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-900 focus:border-blue-900 ${
//                 formikInstance.touched[name] && formikInstance.errors[name] 
//                     ? 'border-red-500' 
//                     : 'border-gray-300'
//             }`}
//         >
//             <option value="">Select {label}</option>
//             {options.map((option) => (
//                 <option key={option} value={option} className="text-xs uppercase font-medium">
//                     {option}
//                 </option>
//             ))}
//         </select>
//         {formikInstance.touched[name] && formikInstance.errors[name] && (
//             <p className="mt-1 text-xs text-red-600 capitalize">{formikInstance.errors[name]}</p>
//         )}
//     </div>
// );

// // Asset Form Component - MOVED OUTSIDE with 2-column grid layout
// const AssetForm = ({ formikInstance, isUpdate = false }) => (
//     <form onSubmit={formikInstance.handleSubmit} className="w-full">
//         {/* Two Column Grid for Form Fields */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//             <FormField label="Asset Name" name="asset_name" formikInstance={formikInstance} />
            
//             <SelectField 
//                 label="Category" 
//                 name="category" 
//                 options={["Yield Crops", "Yield Livestock", "Yield Assets", "Yield Dummy Products"]}
//                 formikInstance={formikInstance}
//             />

//             <FormField 
//                 label="Investing Price" 
//                 name="investing_price" 
//                 type="number" 
//                 formikInstance={formikInstance}
//                 startAdornment="TZS"
//             />

//             <FormField 
//                 label="Total Earning" 
//                 name="total_earning" 
//                 type="number" 
//                 formikInstance={formikInstance}
//                 startAdornment="TZS"
//             />

//             <FormField 
//                 label="Earning Cycle (YRS)" 
//                 name="earning_cycle" 
//                 type="number" 
//                 formikInstance={formikInstance}
//             />

//             <FormField 
//                 label="Lol" 
//                 name="lol" 
//                 type="number" 
//                 formikInstance={formikInstance}
//             />

//             <FormField 
//                 label="Available Quantity" 
//                 name="available_quantity" 
//                 type="number" 
//                 formikInstance={formikInstance}
//                 placeholder="Enter available units"
//             />
//         </div>

//         {/* Image Upload - Full Width */}
//         <div className="w-full mb-4">
//             <label className="block w-full border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors">
//                 <div className="flex flex-col items-center justify-center py-6 px-4">
//                     <FiUpload className="h-8 w-8 text-gray-400 mb-2" />
//                     <p className="text-xs font-medium text-gray-700 capitalize">Click to upload</p>
//                     <p className="text-xs text-gray-400">JPEG, PNG or GIF</p>
//                     <input
//                         type="file"
//                         hidden
//                         name="asset_image"
//                         onChange={(event) => {
//                             formikInstance.setFieldValue('asset_image', event.currentTarget.files[0]);
//                         }}
//                         onBlur={formikInstance.handleBlur}
//                         accept="image/*"
//                     />
//                 </div>
//             </label>
//             {formikInstance.touched.asset_image && formikInstance.errors.asset_image && (
//                 <p className="mt-1 text-xs text-red-600 capitalize">{formikInstance.errors.asset_image}</p>
//             )}
//             {formikInstance.values.asset_image && (
//                 <div className="mt-3">
//                     <p className="text-xs font-medium mb-2">
//                         SELECTED FILE: {formikInstance.values.asset_image.name}
//                     </p>
//                     <img
//                         src={URL.createObjectURL(formikInstance.values.asset_image)}
//                         alt="Preview"
//                         className="max-w-full h-auto max-h-96 rounded"
//                     />
//                 </div>
//             )}
//         </div>

//         {/* Submit Button - Full Width */}
//         <button
//             type="submit"
//             className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3 px-4 rounded text-xs font-medium uppercase transition-colors"
//             disabled={formikInstance.isSubmitting}
//         >
//             {formikInstance.isSubmitting
//                 ? 'Processing...' 
//                 : isUpdate ? 'Update Asset' : 'Add New Asset'
//             }
//         </button>
//     </form>
// );

// function UvunajiAssets() {
//     const queryClient = useQueryClient();
//     const { data: assets, isLoading, isError, refetch } = useQuery({
//        queryKey: ["uvunajiAssets"],
//        queryFn: fetchUvunajiAssets,
//    });

//    const [selectedItem, setSelectedItem] = useState(null);
//     const [open, setOpen] = useState({ create: false, update: false, detail: false });
//     const [toast, setToast] = useState({ open: false, message: "", severity: "" });
//     const [searchQuery, setSearchQuery] = useState("");
//     const [sortBy, setSortBy] = useState("date");
//     const [page, setPage] = useState(0);
//     const [rowsPerPage, setRowsPerPage] = useState(5);

//     const filteredAssets = Array.isArray(assets) ? assets?.filter(item =>
//         item.asset_name?.toLowerCase().includes(searchQuery.toLowerCase())
//     ) : [];

//     const sortedNews = [...filteredAssets].sort((a, b) => {
//         if (sortBy === "asset_name") {
//             return a.asset_name.localeCompare(b.asset_name);
//         } else {
//             return new Date(b.published_at) - new Date(a.published_at);
//         }
//     });

//     const handleChangePage = (newPage) => setPage(newPage);
//     const handleChangeRowsPerPage = (event) => {
//         setRowsPerPage(parseInt(event.target.value, 10));
//         setPage(0);
//     };

//     const mutation = useMutation({
//         mutationFn: createUvunajiAsset,
//         onSuccess: (response) => {
//           queryClient.invalidateQueries({ queryKey: ["uvunajiAssets"] });
//           setOpen({ create: false, update: false, detail:false });
//           setToast({ open: true, message: "New Asset added successfully!", severity: "success" });
//           refetch()
//         },
//         onError: (error) => {
//           setToast({ open: true, message: "Failed to add new Asset. Try again.", severity: "error" });
//         }
//     });

//     const formik = useFormik({
//         initialValues: {
//             asset_name: "",
//             category: "",
//             asset_image: null,
//             investing_price: "",
//             total_earning: "",
//             earning_cycle: "",
//             lol: "",
//             available_quantity: "",
//         },
//         validationSchema: validationSchema,
//         onSubmit: (values) => {
//             mutation.mutate(values);
//         },
//     });
    
//     const updateMutation = useMutation({
//         mutationFn: updateUvunajiAsset,
//         onSuccess: () => {
//             queryClient.invalidateQueries(["uvunajiAssets"]);
//             setOpen({ create: false, update: false, detail:false });
//             setToast({ open: true, message: "Asset updated successfully!", severity: "success" });
//             refetch();
//         },
//         onError: (error) => {
//           setToast({ open: true, message: "Failed to update Asset. Try again.", severity: "error" });
//         }
//     });

//     const deleteMutation = useMutation({
//         mutationFn: deleteUvunajiAsset,
//         onSuccess: () => {
//             queryClient.invalidateQueries(["uvunajiAssets"]);
//             setToast({ open: true, message: "Asset deleted successfully!", severity: "success" });
//             refetch();
//         },
//         onError: (error) => {
//             setToast({ open: true, message: "Failed to delete asset. Try again.", severity: "error" });
//         }
//     });

//     const upformik = useFormik({
//         initialValues: {
//             asset_name: "",
//             category: "",
//             asset_image: null,
//             investing_price: "",
//             total_earning: "",
//             earning_cycle: "",
//             lol: "",
//             available_quantity: "",
//           },
//           validationSchema: upValidationSchema,
//           onSubmit: (values) => { 
//             if (selectedItem.id) {
//             updateMutation.mutate({ id: selectedItem.id, data: values });
//           }
//           },
//     });

//     const handleOpenUpdateDrawer = (item) => {
//         setSelectedItem(item);
//       upformik.setValues({
//         asset_name:item.asset_name || "",
//         category: item.category ||"",
//         asset_image: null,
//         investing_price:item.investing_price || "",
//         total_earning: item.total_earning ||"",
//         earning_cycle:item.earning_cycle || "",
//         lol: item.lol ||"",
//         available_quantity: item.available_quantity || "",
//       });
//         setOpen({ create: false, update: true, detail: false });
//     };

//     const toggleDrawer = (type, state) => () => {
//         setOpen({ create: type === "create" ? state : false, update: type === "update" ? state : false });
//     };

//     const handleDelete = (id) => {
//         deleteMutation.mutate(id);
//     };

//     // Pagination calculations
//     const paginatedData = sortedNews.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
//     const totalPages = Math.ceil(sortedNews.length / rowsPerPage);

//     if (isLoading) {
//         return (
//             <div className="flex items-center justify-center min-h-screen">
//                 <div className="text-center">
//                     <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
//                     <p className="mt-4 text-gray-600">Loading...</p>
//                 </div>
//             </div>
//         );
//     }

//     if (isError) {
//         return (
//             <div className="flex items-center justify-center min-h-screen">
//                 <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
//                     <h3 className="text-red-800 font-semibold mb-2">Error Loading Assets</h3>
//                     <p className="text-red-600 text-sm">Error fetching assets. Please try again later.</p>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
//             {assets?.length === 0 ? (
//                 <div className="w-full">
//                     <h1 className="text-xs font-bold text-blue-900 uppercase mb-1">Add New Asset</h1>
//                     <p className="text-xs text-gray-600 capitalize mb-4">
//                         Fill in the details below to register a new asset, including name, category, image, investment details, and earnings.
//                     </p>
//                     <AssetForm formikInstance={formik} />
//                 </div>
//             ) : open.create ? (
//                 <div className="w-full">
//                     <button
//                         onClick={toggleDrawer("create", false)}
//                         className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
//                     >
//                         <FiArrowLeft className="mr-2" />
//                         <span className="text-xs">Back</span>
//                     </button>
//                     <h1 className="text-xs font-bold text-blue-900 uppercase mb-1">Add New Asset</h1>
//                     <p className="text-xs text-gray-600 capitalize mb-4">
//                         Fill in the details below to register a new asset, including name, category, image, investment details, and earnings.
//                     </p>
//                     <AssetForm formikInstance={formik} />
//                 </div>
//             ) : open.update ? (
//                 <div className="w-full">
//                     <button
//                         onClick={toggleDrawer("update", false)}
//                         className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
//                     >
//                         <FiArrowLeft className="mr-2" />
//                         <span className="text-xs">Back</span>
//                     </button>
//                     <h1 className="text-xs font-bold text-blue-900 uppercase mb-1">Update Asset Details</h1>
//                     <p className="text-xs text-gray-600 capitalize mb-4">
//                         Modify the asset information below, including name, category, image, investment details, and earnings.
//                     </p>
//                     <AssetForm formikInstance={upformik} isUpdate={true} />
//                 </div>
//             ) : (
//                 <>
//                     {/* Header */}
//                     <h1 className="text-xs font-bold text-gray-900 uppercase">Uvunaji Assets Management</h1>
//                     <p className="text-xs text-gray-600 capitalize mb-2">Manage and Publish uvunaji assets</p>
                    
//                     <button
//                         onClick={toggleDrawer("create", true)}
//                         className="mb-2 flex items-center text-blue-900 text-xs font-medium underline hover:no-underline"
//                     >
//                         <FiPlus className="mr-1" />
//                         Click here to Add Asset
//                     </button>

//                     <h2 className="text-xs font-semibold uppercase text-gray-900 mt-4 mb-2">Assets List</h2>

//                     {/* Search and Sort */}
//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//                         <div className="md:col-span-2">
//                             <div className="relative">
//                                 <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
//                                 <input
//                                     type="text"
//                                     placeholder="Search by Asset Name..."
//                                     value={searchQuery}
//                                     onChange={(e) => setSearchQuery(e.target.value)}
//                                     className="w-full pl-10 pr-3 py-2 text-xs bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-900 focus:border-blue-900"
//                                 />
//                             </div>
//                         </div>
//                         <div>
//                             <select
//                                 value={sortBy}
//                                 onChange={(e) => setSortBy(e.target.value)}
//                                 className="w-full px-3 py-2 text-xs bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-900 focus:border-blue-900"
//                             >
//                                 <option value="date">Published Date</option>
//                                 <option value="asset_name">Asset name</option>
//                             </select>
//                         </div>
//                     </div>

//                     {/* Table */}
//                     <div className="overflow-x-auto bg-white rounded-lg shadow">
//                         <table className="min-w-full divide-y divide-gray-200">
//                             <thead className="bg-gray-50">
//                                 <tr>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Asset Name</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Category</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Available Qty</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cycle (YRS)</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Image</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Price (TZS)</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Daily Income</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Net Daily</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Published</th>
//                                     <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
//                                 </tr>
//                             </thead>
//                             <tbody className="bg-white divide-y divide-gray-200">
//                                 {paginatedData.map((asset) => (
//                                     <tr key={asset.id} className="hover:bg-gray-50">
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900 capitalize">{asset.asset_name}</td>
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900 capitalize">{asset.category}</td>
//                                         <td className="px-4 py-3">
//                                             <span className={`text-xs font-bold ${
//                                                 asset.available_quantity === 0 ? 'text-red-600' : 
//                                                 asset.available_quantity < 10 ? 'text-orange-600' : 
//                                                 'text-green-600'
//                                             }`}>
//                                                 {asset.available_quantity || 0} units
//                                             </span>
//                                         </td>
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900">{asset.earning_cycle}</td>
//                                         <td className="px-4 py-3">
//                                             <img
//                                                 src={`${IMG_BASE_URL}${asset.asset_image}`}
//                                                 alt={asset.asset_name}
//                                                 className="w-12 h-12 rounded object-cover"
//                                             />
//                                         </td>
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900">
//                                             {parseFloat(asset.investing_price).toLocaleString()}
//                                         </td>
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900">
//                                             {parseFloat(asset.daily_income).toLocaleString()}
//                                         </td>
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900">
//                                             {parseFloat(asset.net_daily_income).toLocaleString()}
//                                         </td>
//                                         <td className="px-4 py-3 text-xs font-semibold text-gray-900">
//                                             {new Date(asset.published_at).toLocaleDateString()}
//                                         </td>
//                                         <td className="px-4 py-3">
//                                             <div className="flex gap-2">
//                                                 <button
//                                                     onClick={() => handleOpenUpdateDrawer(asset)}
//                                                     className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
//                                                 >
//                                                     <FiEdit2 className="h-4 w-4" />
//                                                 </button>
//                                                 <button
//                                                     onClick={() => handleDelete(asset.id)}
//                                                     disabled={deleteMutation.isLoading}
//                                                     className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
//                                                 >
//                                                     <FiTrash2 className="h-4 w-4" />
//                                                 </button>
//                                             </div>
//                                         </td>
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </table>
//                     </div>

//                     {/* Pagination */}
//                     <div className="mt-4 flex items-center justify-between">
//                         <div className="text-xs text-gray-600">
//                             Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, sortedNews.length)} of {sortedNews.length} results
//                         </div>
//                         <div className="flex items-center gap-2">
//                             <span className="text-xs text-gray-600">Rows per page:</span>
//                             <select
//                                 value={rowsPerPage}
//                                 onChange={handleChangeRowsPerPage}
//                                 className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-900"
//                             >
//                                 <option value={5}>5</option>
//                                 <option value={10}>10</option>
//                                 <option value={25}>25</option>
//                             </select>
//                             <button
//                                 onClick={() => handleChangePage(page - 1)}
//                                 disabled={page === 0}
//                                 className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
//                             >
//                                 <FiChevronLeft className="h-4 w-4" />
//                             </button>
//                             <span className="text-xs text-gray-600">
//                                 {page + 1} / {totalPages || 1}
//                             </span>
//                             <button
//                                 onClick={() => handleChangePage(page + 1)}
//                                 disabled={page >= totalPages - 1}
//                                 className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
//                             >
//                                 <FiChevronRight className="h-4 w-4" />
//                             </button>
//                         </div>
//                     </div>
//                 </>
//             )}

//             {/* Toast Notification */}
//             {toast.open && (
//                 <div className="fixed bottom-4 right-4 z-50">
//                     <div className={`px-4 py-3 rounded shadow-lg ${
//                         toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
//                     } text-white text-xs font-medium capitalize`}>
//                         {toast.message}
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }

// export default UvunajiAssets;